import { faPaintbrush, faBolt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tag } from '@frontend/app/refresh-components';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import React, { useEffect, useMemo } from 'react';
import { ProjectByIdQuery_project_onboardingTemplates } from '@frontend/app/queries/types/ProjectByIdQuery';
import { ProgramOnboardingTemplateType } from '@frontend/app/types/globalTypes';
import { useHistory } from 'react-router-dom';
import { PreviewContainer } from './PreviewContainer';
import { ViewMode } from '../constants';
import { useRecruitment } from '../RecruitmentContext';
import { PreviewBody } from './PreviewBody';
import { SettingsRoutes } from '../../../constants';

export const ApplicationSnapshot = ({ application, projectId }: { application: ProjectByIdQuery_project_onboardingTemplates, projectId: number }) => {
  const { initAmplifyTemplate, initPulseTemplate } = useRecruitment();
  const history = useHistory();

  const timeRange = useMemo(() => {
    if (!application?.startDate) {
      return '';
    }
    const startDate = new Date(application.startDate);
    const endDate = new Date(application.endDate);

    if (application.startDate && !application.endDate) {
      return `${startDate.toLocaleDateString()} - No end date`;
    }

    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  }, [application]);

  useEffect(() => {
    if (application) {
      if (application.templateType === ProgramOnboardingTemplateType.PULSE) {
        initPulseTemplate(application);
      } else {
        initAmplifyTemplate(application);
      }
    }
  }, [application, initAmplifyTemplate, initPulseTemplate]);

  if (!application) {
    return null;
  }

  const onClickCustomizeApplication = () => {
    history.push(SettingsRoutes.recruiting.template.replace(':projectId', projectId.toString()).replace(':templateId', application.id.toString()));
  };

  // Prevent all interactions
  const preventInteractions = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className="flex flex-col gap-4 pointer-events-none select-none"
      onMouseDown={preventInteractions}
      onMouseUp={preventInteractions}
      onClick={preventInteractions}
      onKeyDown={preventInteractions}
      onKeyUp={preventInteractions}
      onKeyPress={preventInteractions}
      tabIndex={-1}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="bg-grey-6 rounded-lg p-2 h-6 w-6 flex items-center justify-center">
          <FontAwesomeIcon icon={faBolt} className="text-white" />
        </div>
        <H3 className="font-medium p-0 m-0">Application Page Snapshot</H3>
      </div>

      <Card className="border border-border shadow-md">
        <CardContent className="p-4 h-[740px] w-full">
          <div className="flex flex-col h-full">
            <div className="flex flex-col gap-2">
              <Tag
                label="Published to Creator Marketplace"
                bgColor="bg-green-2"
                textColor="text-green-6"
                className="w-fit"
              />
              <div className="flex flex-col gap-0">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <h3 className="text-lg font-medium text-grey-6 m-0 cursor-help">
                        {application?.applicationTitle?.length > 30 ? `${application?.applicationTitle?.slice(0, 30)}...` : application?.applicationTitle}
                      </h3>
                    </TooltipTrigger>
                    {application?.applicationTitle?.length > 30 && (
                      <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[300px]">
                        {application.applicationTitle}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                <p className="text-sm text-grey-4 m-0">
                  {timeRange || 'No date range set'}
                </p>
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center">
              <PreviewContainer view={ViewMode.MOBILE}>
                <PreviewBody
                  programTitle={application.applicationTitle}
                  viewMode={ViewMode.MOBILE}
                  termsSettings={null}
                  contentGuideline={null}
                  onSubmit={() => undefined}
                  isSubmitting={false}
                />
              </PreviewContainer>
            </div>

            <div className="mt-2">
              <Button
                variant="primary"
                size="sm"
                className="h-8 gap-2 w-fit pointer-events-auto"
                onClick={onClickCustomizeApplication}
              >
                <FontAwesomeIcon icon={faPaintbrush} />
                Customize
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
